import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../utils/api';
import { Ticket, Plus, Edit, Trash2, X, Search } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  name: '',
  code: '',
  valid_from: todayISO(),
  valid_until_mode: 'always', // 'always' | 'custom'
  valid_until: '',
  discount_type: 'flat', // 'flat' | 'percentage'
  discount_value: '',
  max_cap: '',
  usage_type: 'single', // 'single' | 'multi'
  is_active: true,
});

function formatDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString();
  } catch {
    return s;
  }
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const res = await apiRequest(`${BACKEND_URL}/api/admin/coupons`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.data?.detail || 'Failed to load coupons');
      setCoupons(res.data.coupons || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '',
      code: c.code || '',
      valid_from: c.valid_from || todayISO(),
      valid_until_mode: c.valid_until ? 'custom' : 'always',
      valid_until: c.valid_until || '',
      discount_type: c.discount_type || 'flat',
      discount_value: c.discount_value ?? '',
      max_cap: c.max_cap ?? '',
      usage_type: c.usage_type || 'single',
      is_active: c.is_active !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    // Build payload
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      valid_from: form.valid_from,
      valid_until: form.valid_until_mode === 'always' ? null : form.valid_until || null,
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value || 0),
      max_cap: form.max_cap === '' ? null : Number(form.max_cap),
      usage_type: form.usage_type,
      is_active: !!form.is_active,
    };

    // Client-side validations
    if (!payload.name) {
      setSaving(false);
      setError('Coupon name is required');
      return;
    }
    if (!payload.code) {
      setSaving(false);
      setError('Code is required');
      return;
    }
    if (payload.discount_value <= 0) {
      setSaving(false);
      setError('Discount value must be greater than 0');
      return;
    }
    if (payload.discount_type === 'percentage' && payload.discount_value > 100) {
      setSaving(false);
      setError('Percentage discount cannot exceed 100');
      return;
    }
    if (form.valid_until_mode === 'custom' && !payload.valid_until) {
      setSaving(false);
      setError('Please pick a "Valid Until" date or choose Always');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const url = editing
        ? `${BACKEND_URL}/api/admin/coupons/${editing.id}`
        : `${BACKEND_URL}/api/admin/coupons`;
      const method = editing ? 'PUT' : 'POST';
      const res = await apiRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(res.data?.detail || 'Failed to save coupon');
      closeModal();
      fetchCoupons();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete coupon "${c.name}" (${c.code})?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await apiRequest(`${BACKEND_URL}/api/admin/coupons/${c.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.data?.detail || 'Failed to delete coupon');
      fetchCoupons();
    } catch (err) {
      setError(err.message);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return coupons;
    return coupons.filter((c) =>
      [c.name, c.code, c.discount_type, c.usage_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [coupons, search]);

  const renderDiscount = (c) => {
    if (c.discount_type === 'percentage') {
      return `${c.discount_value}%`;
    }
    return `$${Number(c.discount_value || 0).toLocaleString('en-US')}`;
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
              <Ticket className="w-6 h-6" />
              Coupons
            </h1>
            <p className="text-sm text-gray-500">
              Create and manage discount coupons for orders
            </p>
          </div>
          <button
            onClick={openCreate}
            className="bg-navy text-white px-5 py-2.5 rounded-lg hover:bg-navy/90 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Coupon
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code, type..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
            />
          </div>
        </div>

        {/* Loading / Empty */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading coupons...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
            <Ticket className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">
              {coupons.length === 0 ? 'No Coupons Yet' : 'No Matches'}
            </h3>
            <p className="text-gray-600">
              {coupons.length === 0
                ? 'Click "Add Coupon" to create your first discount coupon.'
                : 'Try a different search term.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Discount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Max Cap</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Usage</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Validity</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-navy">{c.name}</td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                          {c.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="font-semibold text-navy">{renderDiscount(c)}</span>{' '}
                        <span className="text-gray-500 capitalize">({c.discount_type})</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {c.max_cap == null || c.max_cap === '' ? '—' : `$${Number(c.max_cap).toLocaleString('en-US')}`}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${c.usage_type === 'single' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {c.usage_type === 'single' ? 'Single use / user' : 'Multi-use / user'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div>From: {formatDate(c.valid_from)}</div>
                        <div className="text-xs text-gray-500">
                          Until: {c.valid_until ? formatDate(c.valid_until) : 'Always'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${c.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(c)}
                            className="text-navy hover:text-navy/80"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-navy">
                {editing ? 'Edit Coupon' : 'Add Coupon'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Name + Code */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Coupon Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="Diwali Sale 25%"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent font-mono uppercase"
                    placeholder="DIWALI25"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Letters, numbers, '-' and '_' only.</p>
                </div>
              </div>

              {/* Discount type + value */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Type *</label>
                  <div className="flex gap-3">
                    {['flat', 'percentage'].map((t) => (
                      <label key={t} className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="discount_type"
                          value={t}
                          checked={form.discount_type === t}
                          onChange={() => setForm({ ...form, discount_type: t })}
                        />
                        <span className="text-sm capitalize">{t}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Discount Value * {form.discount_type === 'percentage' ? '(%)' : '($)'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={form.discount_type === 'percentage' ? 100 : undefined}
                    step="0.01"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder={form.discount_type === 'percentage' ? '25' : '5'}
                    required
                  />
                </div>
              </div>

              {/* Max cap + usage type */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Max Cap ($, optional)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.max_cap}
                    onChange={(e) => setForm({ ...form, max_cap: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="e.g. 4"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If discount works out higher than this cap, only the cap amount is applied.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Usage per User *</label>
                  <div className="flex gap-3">
                    {[
                      { v: 'single', label: 'Single time' },
                      { v: 'multi', label: 'Multi time' },
                    ].map(({ v, label }) => (
                      <label key={v} className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="usage_type"
                          value={v}
                          checked={form.usage_type === v}
                          onChange={() => setForm({ ...form, usage_type: v })}
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Validity */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Valid From *</label>
                  <input
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Valid Until *</label>
                  <div className="flex gap-3 mb-2">
                    {[
                      { v: 'always', label: 'Always (no expiry)' },
                      { v: 'custom', label: 'Custom date' },
                    ].map(({ v, label }) => (
                      <label key={v} className="flex-1 flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="valid_until_mode"
                          value={v}
                          checked={form.valid_until_mode === v}
                          onChange={() => setForm({ ...form, valid_until_mode: v, valid_until: v === 'always' ? '' : form.valid_until })}
                        />
                        <span className="text-sm">{label}</span>
                      </label>
                    ))}
                  </div>
                  {form.valid_until_mode === 'custom' && (
                    <input
                      type="date"
                      value={form.valid_until}
                      min={form.valid_from || undefined}
                      onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
                      required
                    />
                  )}
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <input
                  id="coupon_is_active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="coupon_is_active" className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-lg bg-navy text-white hover:bg-navy/90 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editing ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
